import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { invoiceService } from '../services/invoiceService';

export function useInvoices(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  initDate?: string;
  endDate?: string;
  storeCode?: string;
  billingPoint?: string;
  cufe?: string;
  customerName?: string;
  documentNumber?: string;
  customerNumberId?: string;
  invoiceStatus?: string;
  documentType?: string;
  companyId?: string;
  // Cursor de escaneo incremental para busquedas con filtro de texto/nombre/
  // estado (ver invoiceSearchCursor.ts en el backend) — undefined = primera
  // pagina. Al incluirlo en el queryKey (via `params`), cada cursor distinto
  // es una query nueva para React Query, asi "Siguiente"/"Anterior" cachean
  // por separado en vez de pisarse.
  cursor?: string;
}) {
  console.log('🔍 useInvoices hook called with params:', params);

  const companyId = params?.companyId;

  return useQuery({
    queryKey: ['invoices', companyId, params], // Include companyId in the key
    queryFn: ({ signal }) => {
      console.log('🔍 useInvoices queryFn executing for company:', companyId);
      console.log('📤 Sending params to API:', params);
      // `signal` lo genera React Query por cada query y lo aborta solo cuando
      // el queryKey cambia (nueva busqueda/pagina) mientras esta todavia
      // seguia en vuelo -- reenviarlo a axios convierte esa cancelacion
      // "logica" (React Query solo ignoraba el resultado) en una cancelacion
      // real de la peticion HTTP, para no seguir cargando Aludra/el BFF con
      // busquedas que el usuario ya reemplazo.
      return invoiceService.getInvoices(params, { signal }); // Send ALL params including companyId
    },
    enabled: !!companyId, // Only fetch if we have a company selected
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    /*
     * Cada pagina/filtro genera un queryKey nuevo (incluye `params` completo),
     * asi que sin esto React Query no tenia datos para la key nueva mientras
     * llegaba la respuesta: `isPending` (y por lo tanto `isLoading`) se volvia
     * true en CADA cambio de pagina, y como el componente hace un `return`
     * temprano en isLoading, toda la pantalla (resumen, tabla, paginacion)
     * se reemplazaba por un spinner de golpe y volvia a aparecer con los
     * datos nuevos — se percibia como que "no se actualizaba" en vez de
     * como una transicion normal de pagina.
     * Con esto, mientras carga la pagina nueva se sigue mostrando la anterior
     * (`isPlaceholderData: true`) y no hay pantalla en blanco de por medio.
     */
    placeholderData: keepPreviousData,
  });
}

export function useInvoicesArchive(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  initDate?: string;
  endDate?: string;
  storeCode?: string;
  billingPoint?: string;
  electronicInvoiceId?: string;
  cufe?: string;
  customerName?: string;
  documentNumber?: string;
  customerNumberId?: string;
  invoiceStatus?: string;
  documentType?: string;
  companyId?: string;
  // Mismo cursor de escaneo incremental que useInvoices — ver comentario ahí.
  cursor?: string;
}) {
  const companyId = params?.companyId;

  return useQuery({
    queryKey: ['invoices', 'archive', companyId, params], // Include companyId in the key
    queryFn: ({ signal }) => {
      console.log('🔍 useInvoicesArchive queryFn executing for company:', companyId);
      console.log('📤 Sending params to API:', params);
      // Mismo motivo que en useInvoices: reenvía el signal de React Query a
      // axios para que cancelar la query cancele tambien la peticion HTTP real.
      return invoiceService.getInvoicesArchive(params, { signal }); // Send ALL params including companyId
    },
    enabled: !!companyId, // Only fetch if we have a company selected
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    // Mismo motivo que en useInvoices: evita que cada cambio de pagina/filtro
    // reemplace toda la pantalla por un spinner mientras llega la respuesta.
    placeholderData: keepPreviousData,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoiceService.getInvoiceById(id),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => invoiceService.createInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useResendToDGI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => invoiceService.resendToDGI(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invoiceService.deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}
